import { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import './Users.scss';
import Table from '../../../components/Table/Table';
import { ReactComponent as Edit } from '../../../assets/edit-3.svg';
import { ReactComponent as Close } from '../../../assets/x.svg';
import { ReactComponent as Refresh } from '../../../assets/refresh-ccw.svg';
import AlertModal from '../../../components/AlertModal/AlertModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import Button from '../../../components/Button/Button';
import { Modal } from '../../../components/Modal/Modal';
import { DynamicInput } from '../../../components/Inputs/Inputs';

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'group_admin', label: 'Group Admin' },
  { value: 'hotel_admin', label: 'Hotel Admin' },
];

export default function Users() {
  const { axiosAuth } = useContext(AuthContext);

  // data
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [hotels, setHotels] = useState([]); // hotéis do grupo selecionado

  // filtro por cliente (grupo) na listagem
  const [clientFilter, setClientFilter] = useState('');
  const [userGroups, setUserGroups] = useState({}); // userId -> [groupId,...]
  const [clientLoading, setClientLoading] = useState(false);

  // ui state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    message: '',
  });
  const [infoModal, setInfoModal] = useState({
    open: false,
    title: '',
    message: '',
  });
  const [confirm, setConfirm] = useState({ open: false, userId: null });

  // envio de link de definição de password para um email à escolha do superadmin
  const [resetLinkModal, setResetLinkModal] = useState({
    open: false,
    user: null,
    email: '',
    sending: false,
  });

  // edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('edit'); // create ou edit
  const initialForm = {
    id: null,
    username: '',
    name: '',
    email: '',
    role: 'hotel_admin',
    password: '',
    confirmPassword: '',

    // associações
    groupId: null, // dropdown quando group_admin/hotel_admin
    hotelIds: [], // checkboxes quando hotel_admin
  };
  const [form, setForm] = useState(initialForm);

  // loaders
  async function fetchUsers() {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/users/');
      setUsers(data || []);
    } catch (err) {
      openError('Erro ao carregar utilizadores', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchGroups() {
    try {
      const { data } = await axiosAuth.get('/groups/');
      setGroups(data || []);
    } catch (err) {
      openError('Erro ao carregar grupos', err);
    }
  }

  async function fetchHotelsByGroup(groupId) {
    if (!groupId) {
      setHotels([]);
      return;
    }
    try {
      const { data } = await axiosAuth.get(`/hotels/group/${groupId}`);
      setHotels(data || []);
    } catch (err) {
      openError('Erro ao carregar hotéis do grupo', err);
    }
  }

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, []);

  // quando o grupo selecionado muda no form, carrega hotéis desse grupo
  useEffect(() => {
    if (form.groupId) {
      fetchHotelsByGroup(form.groupId);
    } else {
      setHotels([]);
    }
  }, [form.groupId]);

  // nome do grupo por id (para a coluna Cliente e o filtro)
  const groupNameById = useMemo(() => {
    const m = {};
    groups.forEach((g) => {
      m[g.id] = g.name;
    });
    return m;
  }, [groups]);

  // constrói o índice utilizador -> grupo(s) (cliente), juntando as associações
  // que hoje só existem em tabelas à parte. group_admin liga direto ao grupo;
  // hotel_admin liga a hotéis, que resolvemos para o grupo a que pertencem.
  useEffect(() => {
    if (!users.length || !groups.length) return;
    let cancelled = false;

    (async () => {
      setClientLoading(true);
      try {
        // 1) hotel_id -> group_id (buscando os hotéis de cada grupo)
        const hotelToGroup = {};
        const perGroup = await Promise.all(
          groups.map((g) =>
            axiosAuth
              .get(`/hotels/group/${g.id}`)
              .then((r) => ({ gid: g.id, hotels: r.data || [] }))
              .catch(() => ({ gid: g.id, hotels: [] })),
          ),
        );
        perGroup.forEach(({ gid, hotels: hs }) => {
          hs.forEach((h) => {
            hotelToGroup[h.id] = gid;
          });
        });

        // 2) para cada utilizador, resolver o(s) grupo(s)
        const entries = await Promise.all(
          users.map(async (u) => {
            try {
              if (u.role === 'group_admin') {
                const { data } = await axiosAuth.get(
                  `/user-group-admins/user/${u.id}`,
                );
                return [u.id, (data || []).map((r) => r.group_id)];
              }
              if (u.role === 'hotel_admin') {
                const { data } = await axiosAuth.get(
                  `/user-hotel-admins/user/${u.id}`,
                );
                const gids = (data || [])
                  .map((r) => hotelToGroup[r.hotel_id])
                  .filter((x) => x != null);
                return [u.id, Array.from(new Set(gids))];
              }
              return [u.id, []]; // superadmin ou sem associação
            } catch {
              return [u.id, []];
            }
          }),
        );

        if (!cancelled) setUserGroups(Object.fromEntries(entries));
      } finally {
        if (!cancelled) setClientLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [users, groups, axiosAuth]);

  function openError(title, err) {
    const msg =
      err?.response?.data?.detail || err?.message || 'Ocorreu um erro';
    setErrorModal({
      open: true,
      title,
      message: msg,
    });
  }

  function closeError() {
    setErrorModal({
      open: false,
      title: '',
      message: '',
    });
  }

  const columns = useMemo(
    () => [
      { key: 'id', label: 'Id' },
      { key: 'username', label: 'Username' },
      { key: 'name', label: 'Nome' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'client', label: 'Cliente' },
      {
        key: 'actions',
        label: 'Ações',
        render: (u) => (
          <>
            <button
              className="action-button edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(u);
              }}
            >
              <Edit />
            </button>
            <button
              className="action-button edit recover"
              onClick={(e) => {
                e.stopPropagation();
                handleRecover(u);
              }}
            >
              <Refresh />
            </button>
            <button
              className="action-button delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(u.id);
              }}
            >
              <Close />
            </button>
          </>
        ),
      },
    ],
    [],
  );

  const tableData = users
    .filter((u) => {
      if (!clientFilter) return true;
      const gids = userGroups[u.id] || [];
      return gids.map(String).includes(String(clientFilter));
    })
    .map((u) => {
      const gids = userGroups[u.id] || [];
      const clientNames = gids
        .map((id) => groupNameById[id])
        .filter(Boolean)
        .join(', ');
      return {
        id: u.id,
        username: u.username,
        name: u.name,
        email: u.email,
        role: u.role,
        client: clientNames || '—',
      };
    });

  // criar
  function handleAdd() {
    setFormMode('create');
    setForm(initialForm);
    setIsModalOpen(true);
  }

  async function createUserFlow() {
    // validações básicas
    if (!form.username.trim() || !form.name.trim() || !form.email.trim()) {
      openError('Validação', new Error('Preencha username, nome e email.'));
      return;
    }
    if (!form.password || form.password.length < 8) {
      openError(
        'Validação',
        new Error('A password deve ter pelo menos 8 caracteres.'),
      );
      return;
    }
    if (form.password !== form.confirmPassword) {
      openError('Validação', new Error('As passwords não coincidem.'));
      return;
    }
    if (form.role === 'group_admin' && !form.groupId) {
      openError('Validação', new Error('Selecione um grupo para Group Admin.'));
      return;
    }
    if (form.role === 'hotel_admin') {
      if (!form.groupId) {
        openError(
          'Validação',
          new Error('Selecione um grupo para Hotel Admin.'),
        );
        return;
      }
      if (!form.hotelIds.length) {
        openError('Validação', new Error('Selecione pelo menos um hotel.'));
        return;
      }
    }

    setSaving(true);
    let createdUserId = null;
    try {
      // cria o utilizador
      const payload = {
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        password: form.password,
      };
      const { data: created } = await axiosAuth.post('/users/', payload);
      createdUserId = created?.id;

      // associações por role
      if (form.role === 'group_admin' && form.groupId) {
        await axiosAuth.post('/user-group-admins/', {
          user_id: createdUserId,
          group_id: form.groupId,
        });
      } else if (
        form.role === 'hotel_admin' &&
        form.groupId &&
        form.hotelIds.length
      ) {
        // criar N relações user-hotel
        for (const hid of form.hotelIds) {
          await axiosAuth.post('/user-hotel-admins/', {
            user_id: createdUserId,
            hotel_id: hid,
          });
        }
      }
      await fetchUsers();
      setIsModalOpen(false);
    } catch (err) {
      // rollback: tenta remover o user criado se as associações falharem
      if (createdUserId) {
        try {
          await axiosAuth.delete(`/users/${createdUserId}`);
        } catch {
          // se o rollback falhar, pelo menos mostramos o erro principal
        }
      }
      openError('Erro ao criar utilizador', err);
    } finally {
      setSaving(false);
    }
  }

  function handleRecover(user) {
    setResetLinkModal({
      open: true,
      user,
      email: user.email || '',
      sending: false,
    });
  }

  async function sendResetLink() {
    const { user, email } = resetLinkModal;
    const target = email.trim();
    if (!target) {
      openError('Validação', new Error('Indique um email de destino.'));
      return;
    }
    setResetLinkModal((p) => ({ ...p, sending: true }));
    try {
      await axiosAuth.post(`/users/${user.id}/send-reset-link`, {
        email: target,
      });
      setResetLinkModal({ open: false, user: null, email: '', sending: false });
      setInfoModal({
        open: true,
        title: 'Link enviado',
        message: `Foi enviado um link de definição de password para ${target}.`,
      });
    } catch (err) {
      setResetLinkModal((p) => ({ ...p, sending: false }));
      openError('Erro ao enviar link', err);
    }
  }

  // editar
  async function handleEdit(user) {
    setFormMode('edit');

    // base do form
    const base = {
      id: user.id,
      username: user.username || '',
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'hotel_admin',
      password: '',
      confirmPassword: '',
      groupId: null,
      hotelIds: [],
    };

    try {
      // pré-preencher associações a partir do backend
      if (user.role === 'group_admin') {
        const { data: ugas } = await axiosAuth.get(
          `/user-group-admins/user/${user.id}`,
        );
        // assume 1 grupo
        base.groupId = ugas?.[0]?.group_id || null;
      } else if (user.role === 'hotel_admin') {
        const { data: uhas } = await axiosAuth.get(
          `/user-hotel-admins/user/${user.id}`,
        );
        const hotelIds = (uhas || []).map((r) => r.hotel_id);
        base.hotelIds = hotelIds;
      }
    } catch (err) {
      // não falhar o modal por causa do pré-carregamento; só informa
      console.warn(
        'Não foi possível pré-carregar associações:',
        err?.response?.data || err?.message,
      );
    }

    setForm(base);
    setIsModalOpen(true);
  }

  async function saveEdit() {
    // validações simples
    if (!form.username.trim() || !form.name.trim() || !form.email.trim()) {
      openError('Validação', new Error('Preencha username, nome e email.'));
      return;
    }
    // password é opcional na edição: só valida se o admin quiser alterá-la
    if (form.password || form.confirmPassword) {
      if (form.password.length < 8) {
        openError(
          'Validação',
          new Error('A nova password deve ter pelo menos 8 caracteres.'),
        );
        return;
      }
      if (form.password !== form.confirmPassword) {
        openError('Validação', new Error('As passwords não coincidem.'));
        return;
      }
    }
    if (form.role === 'group_admin' && !form.groupId) {
      openError('Validação', new Error('Selecione um grupo para Group Admin.'));
      return;
    }
    if (form.role === 'hotel_admin') {
      if (!form.groupId) {
        openError(
          'Validação',
          new Error('Selecione um grupo para Hotel Admin.'),
        );
        return;
      }
      if (!form.hotelIds.length) {
        openError('Validação', new Error('Selecione pelo menos um hotel.'));
        return;
      }
    }

    setSaving(true);

    try {
      // atualizar dados base do user (password só é enviada se preenchida)
      const payload = {
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
      };
      if (form.password) {
        payload.password = form.password;
      }
      await axiosAuth.put(`/users/${form.id}`, payload);

      // sincronizar associações conforme role
      if (form.role === 'superadmin') {
        // limpar todas as relações
        await removeAllGroupRelations(form.id);
        await removeAllHotelRelations(form.id);
      } else if (form.role === 'group_admin') {
        if (!form.groupId) throw new Error('Selecione um grupo.');
        await removeAllHotelRelations(form.id); // não precisa de hotéis
        // manter só 1 grupo
        await removeAllGroupRelations(form.id);
        await axiosAuth.post(`/user-group-admins/`, {
          user_id: form.id,
          group_id: form.groupId,
        });
      } else if (form.role === 'hotel_admin') {
        if (!form.groupId) throw new Error('Selecione um grupo.');
        // sincroniza hotéis: remove todos e volta a criar os selecionados
        await removeAllGroupRelations(form.id);
        await removeAllHotelRelations(form.id);
        for (const hid of form.hotelIds) {
          await axiosAuth.post(`/user-hotel-admins/`, {
            user_id: form.id,
            hotel_id: hid,
          });
        }
      }

      await fetchUsers();
      setIsModalOpen(false);
    } catch (err) {
      openError('Erro ao guardar utilizador', err);
    } finally {
      setSaving(false);
    }
  }

  async function removeAllGroupRelations(userId) {
    try {
      const { data } = await axiosAuth.get(`/user-group-admins/user/${userId}`);
      const ids = (data || []).map((r) => r.group_id);
      // use os novos (backend) DELETES
      await Promise.all(
        ids.map((gid) =>
          axiosAuth.delete(`/user-group-admins/user/${userId}/group/${gid}`),
        ),
      );
    } catch (err) {
      throw err;
    }
  }

  async function removeAllHotelRelations(userId) {
    try {
      const { data } = await axiosAuth.get(`/user-hotel-admins/user/${userId}`);
      const ids = (data || []).map((r) => r.hotel_id);
      await Promise.all(
        ids.map((hid) =>
          axiosAuth.delete(`/user-hotel-admins/user/${userId}/hotel/${hid}`),
        ),
      );
    } catch (err) {
      throw err;
    }
  }

  function handleDelete(userId) {
    setConfirm({ open: true, userId });
  }

  async function confirmDelete() {
    const id = confirm.userId;
    try {
      await axiosAuth.delete(`/users/${id}`);
      await fetchUsers();
    } catch (err) {
      openError('Erro ao apagar utilizador', err);
    } finally {
      setConfirm({ open: false, userId: null });
    }
  }

  return (
    <div className="users-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {infoModal.open && (
        <AlertModal
          variant="info"
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ open: false, title: '', message: '' })}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title="Eliminar utilizador"
          variant="danger"
          message="Deseja eliminar este utilizador?"
          secText="Fechar"
          varText="Remover"
          onCancel={() => setConfirm({ open: false, userId: null })}
          onConfirm={confirmDelete}
        />
      )}
      {resetLinkModal.open && (
        <Modal
          isVisible={resetLinkModal.open}
          onClose={() =>
            setResetLinkModal({ open: false, user: null, email: '', sending: false })
          }
          title={`Enviar link de password — ${resetLinkModal.user?.username || ''}`}
          onSave={sendResetLink}
          saveText="Enviar"
          disableActions={resetLinkModal.sending}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendResetLink();
            }}
          >
            <div className="row">
              <DynamicInput
                type="email"
                label="Enviar para o email"
                name="resetEmail"
                value={resetLinkModal.email}
                onChange={(name, value) =>
                  setResetLinkModal((p) => ({ ...p, email: value }))
                }
                required
                disabled={resetLinkModal.sending}
              />
              <span className="help">
                Por omissão é o email registado do utilizador, mas pode indicar
                outro caso este não tenha acesso a ele.
              </span>
            </div>
          </form>
        </Modal>
      )}
      <PageTitle link="" title="Utilizadores" />
      <div>
        <div className="users-add">
          <div className="users-filters">
            <label htmlFor="client-filter">Cliente</label>
            <select
              id="client-filter"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            >
              <option value="">Todos os clientes</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            {clientLoading && (
              <span className="filter-hint">a carregar clientes…</span>
            )}
          </div>
          <Button type="button" onClick={handleAdd}>
            Novo utilizador
          </Button>
        </div>

        <div className="container">
          <Table columns={columns} data={tableData} />
        </div>

        {isModalOpen && (
          <Modal
            isVisible={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={
              formMode === 'create'
                ? 'Novo utilizador'
                : `Editar ${form.username}`
            }
            onSave={formMode === 'create' ? createUserFlow : saveEdit}
            saveText={formMode === 'create' ? 'Criar' : 'Guardar'}
            disableActions={saving}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                (formMode === 'create' ? createUserFlow : saveEdit)();
              }}
            >
              <>
                <div className="row">
                  <DynamicInput
                    type="text"
                    label="Username"
                    name="username"
                    value={form.username}
                    onChange={(name, value) =>
                      setForm((p) => ({ ...p, username: value }))
                    }
                    required
                    disabled={saving}
                  />
                </div>
                <div className="row">
                  <DynamicInput
                    type="text"
                    label="Nome"
                    name="name"
                    value={form.name}
                    onChange={(name, value) =>
                      setForm((p) => ({ ...p, name: value }))
                    }
                    required
                    disabled={saving}
                  />
                </div>
                <div className="row">
                  <DynamicInput
                    type="email"
                    label="Email"
                    name="email"
                    value={form.email}
                    onChange={(name, value) =>
                      setForm((p) => ({ ...p, email: value }))
                    }
                    required
                    disabled={saving}
                  />
                </div>
                <div className="row roles">
                  <label className="label">Role</label>
                  <select
                    value={form.role}
                    disabled={saving}
                    onChange={(e) => {
                      const role = e.target.value;
                      setForm((p) => ({
                        ...p,
                        role,
                        // reset associações quando muda
                        groupId: null,
                        hotelIds: [],
                      }));
                    }}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
              <>
                <div className="row">
                  <DynamicInput
                    type="password"
                    label={formMode === 'create' ? 'Password' : 'Nova password'}
                    name="password"
                    value={form.password}
                    onChange={(name, value) =>
                      setForm((p) => ({ ...p, password: value }))
                    }
                    required={formMode === 'create'}
                    disabled={saving}
                  />
                  {formMode === 'edit' && (
                    <span className="help">
                      Deixe em branco para não alterar a password.
                    </span>
                  )}
                </div>
                <div className="row">
                  <DynamicInput
                    type="password"
                    label="Confirmar password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={(name, value) =>
                      setForm((p) => ({ ...p, confirmPassword: value }))
                    }
                    required={formMode === 'create'}
                    disabled={saving}
                  />
                </div>
              </>
              {(form.role === 'group_admin' || form.role === 'hotel_admin') && (
                <div className="row roles groups">
                  <label>Grupo</label>
                  <select
                    value={form.groupId || ''}
                    disabled={saving}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        groupId: Number(e.target.value) || null,
                        hotelIds: [],
                      }))
                    }
                  >
                    <option value="">Selecionar um grupo</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {form.role === 'hotel_admin' && form.groupId && (
                <div className="row checkbox">
                  <label className="">Hotéis do grupo</label>
                  <div className="checkbox-grid">
                    {hotels.map((h) => {
                      const checked = form.hotelIds.includes(h.id);
                      return (
                        <label key={h.id} className="checkbox-item">
                          <input
                            type="checkbox"
                            disabled={saving}
                            checked={checked}
                            onChange={(e) => {
                              setForm((p) => {
                                const set = new Set(p.hotelIds);
                                if (e.target.checked) {
                                  set.add(h.id);
                                } else {
                                  set.delete(h.id);
                                }
                                return {
                                  ...p,
                                  hotelIds: Array.from(set),
                                };
                              });
                            }}
                          />
                          {h.name}
                        </label>
                      );
                    })}
                    {hotels.length === 0 && (
                      <div className="help">Sem hotéis neste grupo.</div>
                    )}
                  </div>
                </div>
              )}
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
}
